import React, { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import {
  CHECK_KIND_GROUP,
  CHECK_KIND_LEAF,
  CheckExpression,
  CheckGroup,
  CheckLeaf,
  ConditionNodeData,
  GROUP_CHECK_TYPE,
  OPERATOR_AND,
  WorkflowNode
} from "lib/types/workflow"
import Input from "components/Ui/Form/Input"
import {
  FormSection,
  SaveButton,
  SmallActionButton
} from "./NodeEditorSidebar.styled"
import LeafCheckEditor from "./LeafCheckEditor"
import CheckGroupEditor from "./CheckGroupEditor"
import {
  CUSTOM_HTTP,
  PAYMENT_PROVIDER_NAME,
  isLeafValid,
  makeDefaultLeaf
} from "./conditionFormHelpers"

type Props = {
  node: WorkflowNode
  onSave: (nodeId: string, data: ConditionNodeData) => void
}

interface FormValues {
  label: string
}

const conditionDataToLeaf = (data: ConditionNodeData): CheckLeaf => {
  const checkType = data.checkType || PAYMENT_PROVIDER_NAME
  const isCustomHttp = checkType === CUSTOM_HTTP
  return {
    kind: CHECK_KIND_LEAF,
    checkType,
    comparison: isCustomHttp ? "" : data.comparison || "EQUALS",
    values: data.values ?? [],
    endpoint: data.endpoint,
    customData: data.customData
  }
}

const isExpressionValid = (expr: CheckExpression): boolean => {
  if (expr.kind === CHECK_KIND_LEAF) return isLeafValid(expr)
  if (expr.children.length < 2) return false
  return expr.children.every(isExpressionValid)
}

const ConditionNodeForm = ({ node, onSave }: Props) => {
  const data = node.data as ConditionNodeData

  const [expression, setExpression] = useState<CheckExpression>(() =>
    data.expression ?? conditionDataToLeaf(data)
  )

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: { label: data.label }
  })

  const promoteToGroup = () => {
    if (expression.kind !== CHECK_KIND_LEAF) return
    const group: CheckGroup = {
      kind: CHECK_KIND_GROUP,
      operator: OPERATOR_AND,
      children: [expression, makeDefaultLeaf()]
    }
    setExpression(group)
  }

  const handleGroupChange = (next: CheckGroup) => {
    setExpression(next)
  }

  const handleGroupUnwrap = (child: CheckExpression) => {
    setExpression(child)
  }

  const handleLeafChange = (next: CheckLeaf) => {
    setExpression(next)
  }

  const isValid = useMemo(() => isExpressionValid(expression), [expression])

  const onSubmit = (values: FormValues) => {
    if (!isValid) return

    if (expression.kind === CHECK_KIND_GROUP) {
      onSave(node.id, {
        nodeType: "CONDITION",
        label: values.label,
        checkType: GROUP_CHECK_TYPE,
        comparison: "",
        values: [],
        expression
      })
      return
    }

    const leaf = expression
    onSave(node.id, {
      nodeType: "CONDITION",
      label: values.label,
      checkType: leaf.checkType,
      comparison: leaf.comparison,
      values: leaf.values,
      endpoint: leaf.endpoint,
      customData: leaf.customData,
      expression: undefined
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormSection>
        <Input
          name="label"
          label="Name"
          register={register}
          validation={{
            required: "Name is required",
            maxLength: {
              value: 100,
              message: "Name must be at most 100 characters"
            }
          }}
          errors={errors}
        />
      </FormSection>

      {expression.kind === CHECK_KIND_LEAF ? (
        <>
          <LeafCheckEditor value={expression} onChange={handleLeafChange} />
          <FormSection>
            <SmallActionButton type="button" onClick={promoteToGroup}>
              + Add check (combine with AND/OR)
            </SmallActionButton>
          </FormSection>
        </>
      ) : (
        <CheckGroupEditor
          value={expression}
          onChange={handleGroupChange}
          onUnwrap={handleGroupUnwrap}
        />
      )}

      <SaveButton type="submit" disabled={!isValid}>
        Update Node
      </SaveButton>
    </form>
  )
}

export default ConditionNodeForm
